import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import Button from "components/buttons/Button";
import { t } from "i18next";
import type { FunctionComponent} from "react";
import { useCallback, useMemo } from "react";

import { SelectPlain } from "~/components/formElements/Select/Select.tsx";
import type { FormInputOption } from "~/types/form.ts";

export type MonthAndYearNavigationHandlerPropsType = {
	month: string;
	year: string;
};

type MonthAndYearNavigationProps = {
	month: string;
	monthSelectOptions: FormInputOption[];
	onGoToNextMonthClick: ({ month, year }: MonthAndYearNavigationHandlerPropsType) => void;
	onGoToPrevMonthClick: ({ month, year }: MonthAndYearNavigationHandlerPropsType) => void;
	onGoToTodayClick: () => void;
	onMonthSelectChange: ({ month, year }: MonthAndYearNavigationHandlerPropsType) => void;
	onYearSelectChange: ({ month, year }: MonthAndYearNavigationHandlerPropsType) => void;
	year: string;
	yearSelectOptions: FormInputOption[];
};

const MonthAndYearNavigation: FunctionComponent<MonthAndYearNavigationProps> = ({
	month,
	monthSelectOptions,
	onGoToNextMonthClick,
	onGoToPrevMonthClick,
	onGoToTodayClick,
	onMonthSelectChange,
	onYearSelectChange,
	year,
	yearSelectOptions,
}) => {
	const handleGoToPrevMonthClick = useCallback(() => {
		let prevMonth = parseInt(month);
		let newYear = parseInt(year);

		if (prevMonth < 1) {
			prevMonth = 12;
			newYear--;
		}

		const newYearString = newYear.toString();
		const newMonthString = prevMonth.toString();
		onGoToPrevMonthClick({ month: newMonthString, year: newYearString });
	}, [month, onGoToPrevMonthClick, year]);

	const handleGoToNextMonthClick = useCallback(() => {
		let nextMonth = parseInt(month) + 2;
		let newYear = parseInt(year);

		if (nextMonth > 12) {
			nextMonth = 1;
			newYear++;
		}

		const newYearString = newYear.toString();
		const newMonthString = nextMonth.toString();

		onGoToNextMonthClick({ month: newMonthString, year: newYearString });
	}, [month, onGoToNextMonthClick, year]);

	const handleMonthSelectChange = useCallback(
		(value: string) => {
			const newMonth = (parseInt(value) + 1).toString();
			onMonthSelectChange({ month: newMonth, year });
		},
		[onMonthSelectChange, year],
	);

	const handleYearSelectChange = useCallback(
		(year: string) => {
			onYearSelectChange({ month, year });
		},
		[month, onYearSelectChange],
	);

	const [firstSelectableYear, lastSelectableYear] = useMemo(() => {
	    if(yearSelectOptions){
			return [yearSelectOptions[0].value, yearSelectOptions[yearSelectOptions.length - 1].value]
	    }
	    return []
	    }, [yearSelectOptions]);

	const prevButtonDisabled = firstSelectableYear !== undefined && "0" === month && firstSelectableYear === year;
	const nextButtonDisabled = lastSelectableYear !== undefined && "11" === month && lastSelectableYear === year;

	return (
		<div className="flex flex-row items-center">
			<Button size="sm" theme="none" onClick={handleGoToPrevMonthClick} disabled={prevButtonDisabled}>
				<ChevronLeftIcon className="size-5 hover:fill-accent-600" />
			</Button>
			<div className="flex flex-row gap-1">
				<div className="w-32">
					<SelectPlain value={month} optionsData={monthSelectOptions} onChange={handleMonthSelectChange} />
				</div>
				<div className="w-28">
				<SelectPlain value={year} optionsData={yearSelectOptions} onChange={handleYearSelectChange} />
				</div>
			</div>
			<Button size="sm" theme="none" onClick={handleGoToNextMonthClick} disabled={nextButtonDisabled}>
				<ChevronRightIcon className="size-5 hover:fill-accent-600" />
			</Button>
			<Button
				className="mr-4"
				theme="primary"
				onClick={onGoToTodayClick}
			>
				{t("timetracking.btnCurrentMonth", "Heute")}
			</Button>
		</div>
	);
};

export default MonthAndYearNavigation;
